// Core build: same API minus the bundled session recorder (we never record; record_sessions_percent is 0)
import { useEffect } from 'react';
import mixpanel from 'mixpanel-browser/src/loaders/loader-module-core';
import type { TrackingEventMap, TrackingEventName } from './events';
import { proxiedApiRoutes } from './proxyRoutes';

// Product analytics (Mixpanel) for the public quote funnel. Unrelated to the admin
// business "analytics" module. Every call is a no-op unless explicitly enabled,
// and nothing here may throw into the UI.

let enabled = false;

const MOBILE_BREAKPOINT_PX = 640; // Tailwind `sm`

// DNT / GPC are only honored when VITE_MIXPANEL_RESPECT_PRIVACY_SIGNALS is 'true'
const respectsPrivacySignals = (): boolean =>
  import.meta.env.VITE_MIXPANEL_RESPECT_PRIVACY_SIGNALS === 'true';

const hasPrivacySignal = (): boolean => {
  if (!respectsPrivacySignals()) return false;
  try {
    const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
    return nav.globalPrivacyControl === true || nav.doNotTrack === '1';
  } catch {
    return false;
  }
};

/** Where an event happened: the public quote flow, or a client's event page (and which UI and plan it shows). */
export interface PortalContext {
  client_ui: 'v1' | 'v2';
  /** Cotiza v2 only: the plan the client is looking at or editing */
  plan?: 'main' | 'draft';
}

// Survives resetTracking(): the page the visitor is on, not their identity
let portalContext: PortalContext | null = null;

// Survives resetTracking(): the variant belongs to this visit, not to an identity
let quoteFlow: 'instant' | 'email' | undefined;
let quoteFlowSplit: number | undefined;

const quoteFlowProps = () => ({
  quote_flow: quoteFlow,
  ...(quoteFlowSplit !== undefined && { quote_flow_split: quoteFlowSplit }),
});

const registerSuperProps = () => {
  if (quoteFlow) mixpanel.register(quoteFlowProps());
  mixpanel.register({
    app_env: import.meta.env.VITE_APP_ENV,
    app_surface: portalContext ? 'client_portal' : 'public_quote_flow',
    ...portalContext,
    viewport: window.innerWidth < MOBILE_BREAKPOINT_PX ? 'mobile' : 'desktop',
    locale: navigator.language,
  });
  // UTMs and the initial referrer are captured by the SDK; the entry path is not
  mixpanel.register_once({ first_landing_path: window.location.pathname });
};

// A path like "/ca" targets the first-party proxy in worker.js on the current origin,
// with neutral route names so blocker lists don't match the requests
const resolveApiConfig = (): { api_host?: string; api_routes?: ReturnType<typeof proxiedApiRoutes> } => {
  const host = import.meta.env.VITE_MIXPANEL_API_HOST;
  if (!host) return { api_host: undefined };
  if (!host.startsWith('/')) return { api_host: host };
  return { api_host: `${window.location.origin}${host}`, api_routes: proxiedApiRoutes() };
};

export function initTracking(): void {
  if (enabled) return;
  const token = import.meta.env.VITE_MIXPANEL_TOKEN;
  if (import.meta.env.VITE_MIXPANEL_ENABLED !== 'true' || !token) return;
  // Visitors who ask not to be tracked never get an analytics id
  if (hasPrivacySignal()) return;

  try {
    mixpanel.init(token, {
      ...resolveApiConfig(),
      persistence: 'localStorage',
      track_pageview: false, // explicit events only
      autocapture: false, // click autocapture could pick up typed or displayed PII
      record_sessions_percent: 0, // no session replay
      // The SDK silently drops everything under DNT unless told otherwise; follow our flag instead
      ignore_dnt: !respectsPrivacySignals(),
    });
    registerSuperProps();
    enabled = true;
  } catch {
    enabled = false;
  }
}

export function isTrackingEnabled(): boolean {
  return enabled;
}

export function track<E extends TrackingEventName>(
  event: E,
  props: TrackingEventMap[E],
  options?: { beacon?: boolean },
): void {
  if (!enabled) return;
  try {
    if (options?.beacon) mixpanel.track(event, props, { transport: 'sendBeacon' });
    else mixpanel.track(event, props);
  } catch {
    // analytics must never break the page
  }
}

/** Puts the arm (and the split in effect when it was rolled) on every later event, without tracking an assignment. */
export function restoreQuoteFlow(flow: 'instant' | 'email', split?: number): void {
  quoteFlow = flow;
  if (split !== undefined) quoteFlowSplit = split;
  if (!enabled) return;
  try {
    mixpanel.register(quoteFlowProps());
  } catch {
    // ignore
  }
}

/**
 * Registers the A/B variant as a super property and tracks the assignment.
 * `visit` is the roll when the page opened; `submit` is the server's final decision with its overrides.
 */
export function registerQuoteFlow(
  flow: 'instant' | 'email',
  reason?: string,
  details?: { stage: 'visit' | 'submit'; split?: number },
): void {
  restoreQuoteFlow(flow, details?.split);
  track('Quote Flow Assigned', {
    quote_flow: flow,
    reason,
    stage: details?.stage,
    split: details?.split,
  });
}

/**
 * Marks every later event as sent from a client's event page (`app_surface: 'client_portal'`, `client_ui`, `plan`);
 * `null` when the page is left, back to the public quote flow.
 */
export function setPortalContext(context: PortalContext | null): void {
  portalContext = context;
  if (!enabled) return;
  try {
    if (!context || context.plan === undefined) mixpanel.unregister('plan');
    if (!context) mixpanel.unregister('client_ui');
    registerSuperProps();
  } catch {
    // ignore
  }
}

/** Keeps the portal context on while the calling page is mounted. */
export function usePortalContext(clientUi: PortalContext['client_ui'], plan?: PortalContext['plan']): void {
  useEffect(() => {
    setPortalContext({ client_ui: clientUi, ...(plan ? { plan } : {}) });
  }, [clientUi, plan]);
  useEffect(() => () => setPortalContext(null), []);
}

export function identify(distinctId: string): void {
  if (!enabled) return;
  try {
    mixpanel.identify(distinctId);
  } catch {
    // ignore
  }
}

export function resetTracking(): void {
  if (!enabled) return;
  try {
    mixpanel.reset();
    // reset() also clears super properties
    registerSuperProps();
  } catch {
    // ignore
  }
}

/** Test-only: drop the enabled flag so each test can re-init. */
export function __resetTrackingForTests(): void {
  enabled = false;
  quoteFlow = undefined;
  quoteFlowSplit = undefined;
  portalContext = null;
}
